// MIRRORS: panel/components/confirmDialog/confirmDialog.tsx — identical copy; edit both, or delete this line on both to declare a deliberate split.
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/button/button';
import styles from './confirmDialog.module.scss';

/** The shape every `tr.confirm.*` entry has, so it can be passed straight to confirm(). */
export interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel: string;
  cancelLabel: string;
  /**
   * Defaults to 'danger'. Use 'primary' for consequential-but-reversible
   * actions (pausing a listing) — dressing a one-click-undoable action in the
   * same red as "delete this photo forever" devalues the red everywhere else.
   */
  tone?: 'danger' | 'primary';
}

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Destructive-action prompt. Structurally a sibling of bottomSheet.tsx (portal →
 * backdrop → panel, Escape listener) but deliberately different: role=alertdialog,
 * no drag handle, no header X, and the ONLY exits are the two buttons, Escape and
 * a backdrop click — all three of the latter meaning cancel. Initial focus lands on
 * the safe button, never the destructive one.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Hand focus back to whatever opened the dialog; without this it lands on
  // <body> and a keyboard user loses their place entirely.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    return () => opener?.focus?.();
  }, [open]);

  // Escape cancels; Tab is trapped between the two buttons.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
        return;
      }
      if (e.key !== 'Tab') return;
      const buttons = panelRef.current?.querySelectorAll<HTMLElement>('button');
      if (!buttons || buttons.length === 0) return;
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return createPortal(
    <div className={styles.overlay} onClick={onCancel}>
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        aria-describedby={body ? 'confirmDialogBody' : undefined}
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.title}>{title}</h2>
        {body && (
          <p id="confirmDialogBody" className={styles.body}>
            {body}
          </p>
        )}
        <div className={styles.actions}>
          {/* autoFocus rather than a ref: Button is not a forwardRef component,
              and the dialog only mounts when open, so mount-focus is correct. */}
          <Button variant="secondary" autoFocus onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'primary' ? 'primary' : 'danger'} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
